import React, { useCallback, useEffect, useState } from "react";
import { formatCompact } from "../userFloorHelpers";
import { fetchAdminUserP2P } from "../api/fetchAdminUserP2P";
import {
  p2pContextLine,
  p2pMatchHeadline,
  p2pMatchInitials,
  p2pMetricsLine,
  p2pOutcomeMeta,
  p2pRoleMeta,
  p2pStatusMeta,
  p2pSummaryParts,
} from "../userP2PHelpers";
import UserActivitySurface from "./UserActivitySurface.jsx";
import UserSurfaceRow from "./UserSurfaceRow.jsx";
import { SURFACE_PAGE_SIZE } from "./UserSurfacePager.jsx";
import { surfacePageRange } from "./userSurfacePageHelpers";

export default function UserP2PTab({ userId }) {
  const [duels, setDuels] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: SURFACE_PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ page = 1, refresh = false } = {}) => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAdminUserP2P(userId, { page, limit: SURFACE_PAGE_SIZE, refresh });
        setDuels(data?.duels || []);
        setSummary(data?.summary || null);
        setPagination((prev) => ({
          ...prev,
          ...(data?.pagination || {}),
          limit: data?.pagination?.limit || prev.limit,
        }));
      } catch (err) {
        setDuels([]);
        setSummary(null);
        setError(err.response?.data?.error || err.message || "Failed to load P2P duels");
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    load({ page: 1 });
  }, [userId, load]);

  const { page, totalPages, total, limit } = pagination;
  const { startIndex, endIndex } = surfacePageRange(page, limit, duels.length, total);

  return (
    <UserActivitySurface
      title="P2P duels"
      total={summary?.total ?? total ?? duels.length}
      totalLabel="duels"
      summaryParts={p2pSummaryParts(summary)}
      loading={loading}
      error={error}
      onRefresh={() => load({ page, refresh: true })}
      onRetry={() => load({ page })}
      emptyMessage="No P2P duels linked to this member."
      itemCount={duels.length}
      pager={{
        page,
        totalPages,
        total,
        startIndex,
        endIndex,
        loading,
        onPageChange: (nextPage) => load({ page: nextPage }),
        emptyLabel: "No duels",
      }}
    >
      {duels.map((duel, idx) => {
        const status = p2pStatusMeta(duel.status);
        const role = p2pRoleMeta(duel.role);
        const outcome = p2pOutcomeMeta(duel.p2pStatus);
        const showOutcome =
          duel.status === "Settled" || duel.p2pStatus === "Won" || duel.p2pStatus === "Lost";
        const tags = [
          { label: status.label, tone: status.tone },
          { label: role.label, tone: role.tone },
        ];
        if (showOutcome) tags.push({ label: outcome.label, tone: outcome.tone });
        return (
          <UserSurfaceRow
            key={duel._id}
            rank={startIndex + idx + 1}
            initials={p2pMatchInitials(duel.matchHome, duel.matchAway)}
            name={p2pMatchHeadline(duel)}
            tags={tags}
            context={p2pContextLine(duel)}
            metrics={p2pMetricsLine(duel, formatCompact)}
          />
        );
      })}
    </UserActivitySurface>
  );
}
